import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import DashboardTemplate from '../../Common/DashboardTemplate';
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@material-ui/core';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import 'filepond/dist/filepond.min.css';

import { ServiceLocator } from '../../../Services/ServiceLocator';
import { AuthenticationService } from '../../../Services/AuthenticationService';
import { getNumberInputDisplayValue, parseNumberInputValue } from '../../../utils/numberInputUtils';
import { formatUtcForDisplay, toUtcForStorage } from '../../../utils/dateUtils';
import { getShipmentBoxesForPackingSlip, getBoxNumberFromName, isPackingSlipFullyReceived } from '../../../utils/packingSlipShipmentUtils';
import { followAppLink } from '../../../utils/appLink';
import PartDisplay from '../../Common/PartDisplay';
import { PurchaseLineLink } from '../../Common/PurchaseLineLink';
import PackingSlipDetails from './PackingSlipDetails';

registerPlugin(FilePondPluginFileValidateSize);

const DEFAULT_OUTSTANDING_PARTS_SORT_BY = 'purchaseOrderNumber';
const DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION = 'asc';

const styles = () => ({});

const cloneBoxSelections = (boxesSelectedParts, boxesSelectedPartsQuantities, boxNumber) => ({
    selectedParts: { ...(boxesSelectedParts[boxNumber] || {}) },
    selectedQuantities: { ...(boxesSelectedPartsQuantities[boxNumber] || {}) },
});

let globalProps;

class EditPackingSlipBox extends React.Component {
    state = {
        loading: true,
        saving: false,
        user: null,
        permissions: [],
        packingSlipId: null,
        packingSlip: null,
        shipmentBaseName: null,
        shipmentBoxes: [],
        isMultiBoxShipment: false,
        currentBoxNumber: 1,
        numberOfBoxes: 1,
        packingSlips: [],
        parts: [],
        outstandingParts: [],
        packingSlipName: '',
        packingSlipTrackingNumber: '',
        selectedVendor: '',
        boxesSelectedParts: { 1: {} },
        boxesSelectedPartsQuantities: { 1: {} },
        packingSlipFiles: [],
        originalFileUrl: '',
        originalFileName: '',
        sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
        sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
        partsSearchTerm: '',
        showSelected: false,
        overrideValidation: false,
        screen: 'view',
        viewingBoxId: null,
        partFamilies: [],
        receivingBoxId: null,
        receivingQuantities: {},
        isReceiving: false,
        shipmentReceiveMode: 'per-box',
        deletingShipment: false,
        receivingActionsByBox: {},
        editingReceivingActionId: null,
        editingReceivingAction: {},
        loadingOutstandingParts: false,
        lastDataRefresh: null,
        initialSnapshot: null,
    };

    constructor(props) {
        super(props);
        globalProps = props;
        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common.Authorization = `Bearer ${jwtToken}`;
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.match?.params?.packingSlipId;
        const nextId = this.props.match?.params?.packingSlipId;
        if (prevId !== nextId && nextId) {
            if (this.skipNextBoxLoad === nextId) {
                this.skipNextBoxLoad = null;
                return;
            }
            this.loadData();
        }
    }

    loadData = async () => {
        const packingSlipId = this.props.match?.params?.packingSlipId;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Purchase')) {
            globalProps.history.push('/login');
            return;
        }

        this.setState({ loading: true, packingSlipId });

        try {
            const partService = ServiceLocator.Instance.getPartService();
            const [packingSlips, parts, partFamilies, packingSlip] = await Promise.all([
                partService.getAllPackingSlips(),
                partService.getAllPartsExpanded(),
                partService.getPartFamilies(),
                partService.getPurchaseOrderPackingSlipById(packingSlipId),
            ]);

            if (!packingSlip) {
                window.$alertError('Packing slip box not found.');
                globalProps.history.push('/admin/packing-slips');
                return;
            }

            const shipment = getShipmentBoxesForPackingSlip(packingSlips, packingSlip);
            const currentBox = shipment.boxes.find((box) => box.id === packingSlipId) || packingSlip;
            const boxesSelectedParts = {};
            const boxesSelectedPartsQuantities = {};

            shipment.boxes.forEach((box) => {
                const boxNumber = shipment.isMultiBox
                    ? getBoxNumberFromName(box.name)
                    : 1;
                const selectedParts = {};
                const selectedPartsQuantities = {};
                (box.items || []).forEach((item) => {
                    selectedParts[item.purchasedPartId] = true;
                    selectedPartsQuantities[item.purchasedPartId] = item.quantityExpected;
                });
                boxesSelectedParts[boxNumber] = selectedParts;
                boxesSelectedPartsQuantities[boxNumber] = selectedPartsQuantities;
            });

            const shipmentName = shipment.isMultiBox ? shipment.baseName : currentBox.name;
            const shipmentTracking = (shipment.boxes[0] && shipment.boxes[0].trackingNumber) || currentBox.trackingNumber || '';
            const initialSnapshot = {
                packingSlipName: shipmentName,
                packingSlipTrackingNumber: shipmentTracking,
                numberOfBoxes: shipment.isMultiBox ? shipment.boxes.length : 1,
                boxesSelectedParts: JSON.parse(JSON.stringify(boxesSelectedParts)),
                boxesSelectedPartsQuantities: JSON.parse(JSON.stringify(boxesSelectedPartsQuantities)),
            };

            await new Promise((resolve) => {
                this.setState({
                    loading: true,
                    user,
                    permissions: user.userPermissions,
                    packingSlip: currentBox,
                    shipmentBaseName: shipment.baseName,
                    shipmentBoxes: shipment.boxes,
                    isMultiBoxShipment: shipment.isMultiBox,
                    currentBoxNumber: shipment.boxNumber,
                    numberOfBoxes: shipment.isMultiBox ? shipment.boxes.length : 1,
                    packingSlips,
                    parts,
                    partFamilies: partFamilies || [],
                    packingSlipName: shipmentName,
                    packingSlipTrackingNumber: shipmentTracking,
                    selectedVendor: currentBox.vendorName,
                    boxesSelectedParts,
                    boxesSelectedPartsQuantities,
                    packingSlipFiles: [],
                    originalFileUrl: currentBox.fileUrl || '',
                    originalFileName: currentBox.fileName || '',
                    sortBy: DEFAULT_OUTSTANDING_PARTS_SORT_BY,
                    sortDirection: DEFAULT_OUTSTANDING_PARTS_SORT_DIRECTION,
                    partsSearchTerm: '',
                    showSelected: false,
                    overrideValidation: false,
                    lastDataRefresh: new Date(),
                    initialSnapshot,
                }, resolve);
            });

            if (this.props.match?.params?.packingSlipId !== packingSlipId) return;

            const partsPromise = currentBox.vendorName
                ? this.loadAllPurchasedPartsForVendor(currentBox.vendorName)
                : Promise.resolve();
            this.loadReceivingActions(shipment.boxes);
            await partsPromise;

            if (this.props.match?.params?.packingSlipId !== packingSlipId) return;
            this.setState({ loading: false });
        } catch (error) {
            console.error('Error loading packing slip box:', error);
            window.$alertError('Error loading packing slip box.');
            this.setState({ loading: false });
        }
    };

    loadAllPurchasedPartsForVendor = (vendorName) => {
        const partService = ServiceLocator.Instance.getPartService();
        this.setState({ loadingOutstandingParts: true });

        return partService.getAllPurchasedPartsByVendor(vendorName).then(async (allPurchasedParts) => {
            const partsWithOtherSlipsQuantity = allPurchasedParts.map((part) => ({
                ...part,
                quantityOnOtherSlips: this.calculateQuantityOnOtherSlips(part.id),
            }));

            const currentlySelectedPartIds = new Set();
            this.state.shipmentBoxes.forEach((box) => {
                (box.items || []).forEach((item) => {
                    currentlySelectedPartIds.add(item.purchasedPartId);
                });
            });

            const filteredParts = partsWithOtherSlipsQuantity.filter((part) => {
                if (currentlySelectedPartIds.has(part.id)) return true;
                return part.quantityAvailable > 0;
            });

            await new Promise((resolve) => {
                this.setState({
                    outstandingParts: this.sortOutstandingParts(filteredParts),
                    loadingOutstandingParts: false,
                }, resolve);
            });
        }).catch((error) => {
            console.error('Error loading purchased parts:', error);
            return new Promise((resolve) => {
                this.setState({ loadingOutstandingParts: false }, resolve);
            });
        });
    };

    calculateQuantityOnOtherSlips = (purchasedPartId) => {
        let totalOnOtherSlips = 0;
        this.state.packingSlips.forEach((packingSlip) => {
            const isPartOfEditingShipment = this.state.shipmentBoxes.some((box) => box.id === packingSlip.id);
            if (isPartOfEditingShipment) return;

            (packingSlip.items || []).forEach((item) => {
                if (item.purchasedPartId === purchasedPartId) {
                    totalOnOtherSlips += item.quantityExpected || 0;
                }
            });
        });
        return totalOnOtherSlips;
    };

    getOutstandingPartSortKey = (part) => (part.partNumber || part.partFamilyName || '').split(' rev ')[0].trim();

    compareOutstandingParts = (a, b, sortBy, sortDirection) => {
        const dir = sortDirection === 'asc' ? 1 : -1;
        const comparePartNumbers = () => this.getOutstandingPartSortKey(a).localeCompare(
            this.getOutstandingPartSortKey(b),
            undefined,
            { numeric: true, sensitivity: 'base' }
        );
        const comparePoNumbers = () => String(a.purchaseOrderNumber || '').localeCompare(
            String(b.purchaseOrderNumber || ''),
            undefined,
            { numeric: true, sensitivity: 'base' }
        );
        const compareOrderDates = () => {
            const aTime = new Date(a.orderDate).getTime() || 0;
            const bTime = new Date(b.orderDate).getTime() || 0;
            return aTime - bTime;
        };

        if (sortBy === 'purchaseOrderNumber') {
            const poCompare = comparePoNumbers();
            if (poCompare !== 0) return poCompare * dir;
            return comparePartNumbers();
        }

        let result = 0;
        if (sortBy === 'partNumber') result = comparePartNumbers();
        else if (sortBy === 'orderDate') result = compareOrderDates();
        return result * dir;
    };

    sortOutstandingParts = (parts) => {
        const { sortBy, sortDirection } = this.state;
        return [...parts].sort((a, b) => this.compareOutstandingParts(a, b, sortBy, sortDirection));
    };

    handleSort = (column) => {
        const { sortBy, sortDirection } = this.state;
        const newDirection = sortBy === column && sortDirection === 'asc' ? 'desc' : 'asc';
        this.setState({
            sortBy: column,
            sortDirection: newDirection,
            outstandingParts: [...this.state.outstandingParts].sort((a, b) => this.compareOutstandingParts(a, b, column, newDirection)),
        });
    };

    partMatchesPartsSearchTerm = (part, searchTerms) => searchTerms.every((term) => {
        const searchTerm = term.toLowerCase();
        return (part.partNumber && part.partNumber.toLowerCase().includes(searchTerm))
            || (part.partFamilyName && part.partFamilyName.toLowerCase().includes(searchTerm))
            || (part.purchaseOrderNumber && part.purchaseOrderNumber.toString().toLowerCase().includes(searchTerm))
            || (part.partDescription && part.partDescription.toLowerCase().includes(searchTerm));
    });

    getPartsSearchTerms = () => (this.state.partsSearchTerm || '').split(' ').filter((term) => term.trim() !== '');

    filterOutstandingParts = (parts) => {
        let filteredParts = parts;
        const searchTerms = this.getPartsSearchTerms();
        if (searchTerms.length > 0) {
            filteredParts = filteredParts.filter((part) => this.partMatchesPartsSearchTerm(part, searchTerms));
        }
        if (this.state.showSelected) {
            filteredParts = filteredParts.filter((part) => this.state.boxesSelectedParts[this.state.currentBoxNumber]?.[part.id]);
        }
        return filteredParts;
    };

    getAllSelectedParts = () => {
        const allSelectedParts = {};
        const allSelectedQuantities = {};
        Object.keys(this.state.boxesSelectedParts).forEach((boxNumber) => {
            const boxIndex = parseInt(boxNumber, 10);
            if (boxIndex < 1 || boxIndex > this.state.numberOfBoxes) return;
            const boxParts = this.state.boxesSelectedParts[boxNumber];
            const boxQuantities = this.state.boxesSelectedPartsQuantities[boxNumber];
            Object.keys(boxParts || {}).forEach((partId) => {
                if (!boxParts[partId]) return;
                if (!allSelectedParts[partId]) {
                    allSelectedParts[partId] = {};
                    allSelectedQuantities[partId] = 0;
                }
                allSelectedParts[partId][boxNumber] = true;
                allSelectedQuantities[partId] += parseNumberInputValue(boxQuantities[partId]) || 0;
            });
        });
        return { allSelectedParts, allSelectedQuantities };
    };

    getSavedShipmentQuantity = (partId) => {
        let total = 0;
        (this.state.shipmentBoxes || []).forEach((box) => {
            (box.items || []).forEach((item) => {
                if (item.purchasedPartId === partId) {
                    total += item.quantityExpected || 0;
                }
            });
        });
        return total;
    };

    getOriginalAvailableQuantity = (partId) => {
        const part = this.state.outstandingParts.find((p) => p.id === partId);
        if (!part) return 0;
        return Math.max(0, part.quantityAvailable + this.getSavedShipmentQuantity(partId));
    };

    getAdjustedOnOtherSlipsQuantity = (partId) => {
        const part = this.state.outstandingParts.find((p) => p.id === partId);
        if (!part) return 0;
        let onOtherSlipsQuantity = part.quantityOnOtherSlips || 0;
        for (let boxNumber = 1; boxNumber <= this.state.numberOfBoxes; boxNumber += 1) {
            if (boxNumber !== this.state.currentBoxNumber) {
                onOtherSlipsQuantity += parseInt(this.state.boxesSelectedPartsQuantities[boxNumber]?.[partId] || '0', 10) || 0;
            }
        }
        return onOtherSlipsQuantity;
    };

    getAdjustedAvailableQuantity = (partId) => {
        const part = this.state.outstandingParts.find((p) => p.id === partId);
        if (!part) return 0;
        let availableQuantity = part.quantityAvailable + this.getSavedShipmentQuantity(partId);
        for (let boxNumber = 1; boxNumber <= this.state.numberOfBoxes; boxNumber += 1) {
            if (boxNumber !== this.state.currentBoxNumber) {
                availableQuantity -= parseInt(this.state.boxesSelectedPartsQuantities[boxNumber]?.[partId] || '0', 10) || 0;
            }
        }
        return Math.max(0, availableQuantity);
    };

    getAvailableQuantityForValidation = (partId) => this.getOriginalAvailableQuantity(partId);

    getQuantityOverages = () => {
        const { allSelectedQuantities } = this.getAllSelectedParts();
        const overages = {};
        Object.keys(allSelectedQuantities).forEach((partId) => {
            const availableQuantity = this.getOriginalAvailableQuantity(partId);
            if (allSelectedQuantities[partId] > availableQuantity) {
                overages[partId] = {
                    totalSelected: allSelectedQuantities[partId],
                    available: availableQuantity,
                    overage: allSelectedQuantities[partId] - availableQuantity,
                };
            }
        });
        return overages;
    };

    isPartOveraged = (partId) => !!this.getQuantityOverages()[partId];

    getValidationErrors = () => {
        const overages = this.getQuantityOverages();
        return Object.keys(overages).map((partId) => {
            const part = this.state.outstandingParts.find((p) => p.id === partId);
            const overage = overages[partId];
            return {
                partId,
                partNumber: part?.partNumber || part?.partFamilyName || 'Unknown',
                revision: part?.partId ? (this.state.parts.find((x) => x.part.id === part.partId)?.part?.revision || '-') : '-',
                message: `Total quantity selected (${overage.totalSelected}) exceeds available quantity (${overage.available}) by ${overage.overage}`,
            };
        });
    };

    handleBoxPartSelect = (partId, isSelected) => {
        const boxNumber = this.state.currentBoxNumber;
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        if (!boxesSelectedParts[boxNumber]) boxesSelectedParts[boxNumber] = {};
        if (!boxesSelectedPartsQuantities[boxNumber]) boxesSelectedPartsQuantities[boxNumber] = {};

        if (isSelected) {
            boxesSelectedParts[boxNumber][partId] = true;
            boxesSelectedPartsQuantities[boxNumber][partId] = this.getAdjustedAvailableQuantity(partId);
        } else {
            delete boxesSelectedParts[boxNumber][partId];
            delete boxesSelectedPartsQuantities[boxNumber][partId];
        }
        this.setState({ boxesSelectedParts, boxesSelectedPartsQuantities });
    };

    handleBoxPartQuantityChange = (partId, quantity) => {
        const boxNumber = this.state.currentBoxNumber;
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        if (!boxesSelectedPartsQuantities[boxNumber]) boxesSelectedPartsQuantities[boxNumber] = {};
        boxesSelectedPartsQuantities[boxNumber][partId] = quantity;
        this.setState({ boxesSelectedPartsQuantities });
    };

    handleSelectAll = () => {
        const boxNumber = this.state.currentBoxNumber;
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        if (!boxesSelectedParts[boxNumber]) boxesSelectedParts[boxNumber] = {};
        if (!boxesSelectedPartsQuantities[boxNumber]) boxesSelectedPartsQuantities[boxNumber] = {};

        let partsToSelect = this.state.outstandingParts;
        const searchTerms = this.getPartsSearchTerms();
        if (searchTerms.length > 0) {
            partsToSelect = partsToSelect.filter((part) => this.partMatchesPartsSearchTerm(part, searchTerms));
        }

        partsToSelect.forEach((part) => {
            boxesSelectedParts[boxNumber][part.id] = true;
            boxesSelectedPartsQuantities[boxNumber][part.id] = this.getAdjustedAvailableQuantity(part.id);
        });
        this.setState({ boxesSelectedParts, boxesSelectedPartsQuantities });
    };

    handleSelectNone = () => {
        const boxNumber = this.state.currentBoxNumber;
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        boxesSelectedParts[boxNumber] = {};
        boxesSelectedPartsQuantities[boxNumber] = {};
        this.setState({ boxesSelectedParts, boxesSelectedPartsQuantities });
    };

    uploadEditedPackingSlipFiles = async (primaryPackingSlipId, allBoxIds = []) => {
        if (!this.state.packingSlipFiles?.length || !primaryPackingSlipId) return;
        const partService = ServiceLocator.Instance.getPartService();
        const file = this.state.packingSlipFiles[0];
        try {
            await partService.uploadPackingSlipFile(primaryPackingSlipId, file);
            for (const boxId of allBoxIds) {
                if (boxId !== primaryPackingSlipId) {
                    try {
                        await partService.clonePackingSlipFileFromOtherSlip(boxId, primaryPackingSlipId);
                    } catch (error) {
                        console.error('Error cloning uploaded file to box:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Error uploading packing slip file:', error);
            window.$alertWarning('Packing slip saved, but the file upload failed. Try uploading again.');
        }
    };

    handleBoxTabChange = (boxNumber) => {
        this.setState({ currentBoxNumber: boxNumber });
        const box = (this.state.shipmentBoxes || []).find((entry) => getBoxNumberFromName(entry.name) === boxNumber);
        if (box?.id && this.props.history) {
            this.skipNextBoxLoad = box.id;
            this.props.history.replace(`/admin/packing-slips/box/${box.id}`);
        }
    };

    handleNumberOfBoxesChange = (numberOfBoxes) => {
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        for (let boxNumber = 1; boxNumber <= numberOfBoxes; boxNumber += 1) {
            if (!boxesSelectedParts[boxNumber]) boxesSelectedParts[boxNumber] = {};
            if (!boxesSelectedPartsQuantities[boxNumber]) boxesSelectedPartsQuantities[boxNumber] = {};
        }
        Object.keys(boxesSelectedParts).forEach((boxNumber) => {
            if (parseInt(boxNumber, 10) > numberOfBoxes) {
                delete boxesSelectedParts[boxNumber];
                delete boxesSelectedPartsQuantities[boxNumber];
            }
        });
        this.setState({
            numberOfBoxes,
            isMultiBoxShipment: numberOfBoxes > 1,
            boxesSelectedParts,
            boxesSelectedPartsQuantities,
            currentBoxNumber: Math.min(this.state.currentBoxNumber, numberOfBoxes),
        });
    };

    cloneSelectionsOntoBox = (sourceBoxNumber, targetBoxNumber) => {
        const boxesSelectedParts = { ...this.state.boxesSelectedParts };
        const boxesSelectedPartsQuantities = { ...this.state.boxesSelectedPartsQuantities };
        const sourceParts = boxesSelectedParts[sourceBoxNumber] || {};
        const sourceQuantities = boxesSelectedPartsQuantities[sourceBoxNumber] || {};
        const allocatedQuantities = {};

        Object.keys(boxesSelectedParts).forEach((boxNum) => {
            if (parseInt(boxNum, 10) === targetBoxNumber) return;
            const boxParts = boxesSelectedParts[boxNum] || {};
            const boxQuantities = boxesSelectedPartsQuantities[boxNum] || {};
            Object.keys(boxParts).forEach((partId) => {
                if (!boxParts[partId]) return;
                allocatedQuantities[partId] = (allocatedQuantities[partId] || 0) + (parseNumberInputValue(boxQuantities[partId]) || 0);
            });
        });

        const newTargetParts = {};
        const newTargetQuantities = {};
        const reducedQuantityWarnings = [];
        const skippedParts = [];

        Object.keys(sourceParts).forEach((partId) => {
            if (!sourceParts[partId]) return;
            const sourceQuantity = parseNumberInputValue(sourceQuantities[partId]) || 0;
            const part = this.state.outstandingParts.find((entry) => entry.id === partId);
            if (!part) return;
            const remainingAvailable = Math.max(0, this.getOriginalAvailableQuantity(partId) - (allocatedQuantities[partId] || 0));
            if (remainingAvailable > 0) {
                const clonedQuantity = Math.min(sourceQuantity, remainingAvailable);
                newTargetParts[partId] = true;
                newTargetQuantities[partId] = clonedQuantity;
                if (clonedQuantity < sourceQuantity) {
                    reducedQuantityWarnings.push(`${part.partNumber || part.partFamilyName}: Reduced from ${sourceQuantity} to ${clonedQuantity} (${remainingAvailable} available)`);
                }
            } else {
                skippedParts.push(`${part.partNumber || part.partFamilyName}: ${sourceQuantity} requested, 0 available`);
            }
        });

        boxesSelectedParts[targetBoxNumber] = newTargetParts;
        boxesSelectedPartsQuantities[targetBoxNumber] = newTargetQuantities;
        this.setState({ boxesSelectedParts, boxesSelectedPartsQuantities });

        if (reducedQuantityWarnings.length > 0) {
            window.$alertWarning(`Some quantities were reduced due to limited availability:\n${reducedQuantityWarnings.join('\n')}`);
        }
        if (skippedParts.length > 0) {
            window.$alertWarning(`${skippedParts.length} parts were not cloned due to no remaining quantity available:\n${skippedParts.join('\n')}`);
        }
    };

    handleSave = async () => {
        if (!this.state.packingSlipName.trim()) {
            window.$alertWarning('Please enter a packing slip name');
            return;
        }

        const validationErrors = this.getValidationErrors();
        if (validationErrors.length > 0 && !this.state.overrideValidation) {
            window.$alertWarning('Cannot update packing slip: Some parts exceed available quantities. Review the highlighted errors or enable override.');
            return;
        }

        if (validationErrors.length > 0 && this.state.overrideValidation) {
            const confirmed = window.confirm(
                `You are about to update this box with quantities that exceed available orders.\n\nAffected parts:\n${validationErrors.map((error) => `- ${error.partNumber} rev ${error.revision}: ${error.message}`).join('\n')}`
            );
            if (!confirmed) return;
        }

        const boxesToProcess = [];
        for (let boxNumber = 1; boxNumber <= this.state.numberOfBoxes; boxNumber += 1) {
            const boxSelectedParts = this.state.boxesSelectedParts[boxNumber] || {};
            const boxSelectedQuantities = this.state.boxesSelectedPartsQuantities[boxNumber] || {};
            const selectedPartIds = Object.keys(boxSelectedParts).filter((id) => boxSelectedParts[id]);
            if (selectedPartIds.length === 0) continue;

            for (const partId of selectedPartIds) {
                const quantity = parseNumberInputValue(boxSelectedQuantities[partId]);
                const part = this.state.outstandingParts.find((entry) => entry.id === partId);
                const availableQuantityForValidation = this.getAvailableQuantityForValidation(partId);
                if (!part || !quantity || quantity <= 0 || (quantity > availableQuantityForValidation && !this.state.overrideValidation)) {
                    window.$alertWarning(`Box ${boxNumber}: Please enter a valid quantity for ${part?.partNumber || part?.partFamilyName || 'selected part'} (1-${availableQuantityForValidation})`);
                    return;
                }
            }

            boxesToProcess.push({ boxNumber, selectedPartIds, quantities: boxSelectedQuantities });
        }

        if (boxesToProcess.length === 0) {
            window.$alertWarning('Please select at least one part in any box');
            return;
        }

        const { allSelectedQuantities } = this.getAllSelectedParts();
        for (const [partId, totalQuantity] of Object.entries(allSelectedQuantities)) {
            const part = this.state.outstandingParts.find((entry) => entry.id === partId);
            const originalAvailableQuantity = this.getOriginalAvailableQuantity(partId);
            if (part && totalQuantity > originalAvailableQuantity && !this.state.overrideValidation) {
                window.$alertWarning(`Total quantity for ${part.partNumber || part.partFamilyName || 'selected part'} across all boxes (${totalQuantity}) exceeds available quantity (${originalAvailableQuantity}). Enable override to save anyway.`);
                return;
            }
        }

        this.setState({ saving: true });
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const baseName = this.state.packingSlipName.trim();
            const isMulti = this.state.numberOfBoxes > 1;
            const savedBoxIds = [];
            let primaryBoxId = null;

            for (const box of boxesToProcess) {
                const boxName = isMulti ? `${baseName} - Box ${box.boxNumber}` : baseName;
                const existingBox = isMulti
                    ? (this.state.shipmentBoxes || []).find((entry) => getBoxNumberFromName(entry.name) === box.boxNumber)
                    : this.state.packingSlip;
                const items = box.selectedPartIds.map((partId) => {
                    const existingItem = existingBox?.items?.find((item) => item.purchasedPartId === partId);
                    return {
                        id: existingItem?.id || uuid(),
                        purchasedPartId: partId,
                        quantityExpected: parseNumberInputValue(box.quantities[partId]),
                        quantityReceived: existingItem?.quantityReceived || 0,
                    };
                });

                if (existingBox?.id) {
                    await partService.updatePackingSlip({
                        id: existingBox.id,
                        name: boxName,
                        trackingNumber: this.state.packingSlipTrackingNumber || null,
                        vendorName: this.state.selectedVendor,
                        dateCreated: existingBox.dateCreated || toUtcForStorage(new Date()),
                        isReceived: existingBox.isReceived || false,
                        userId: existingBox.userId || this.state.user.id,
                        items,
                    });

                    const existingItemIds = new Set((existingBox.items || []).map((item) => item.id));
                    const newItemIds = new Set(items.map((item) => item.id));
                    for (const existingItem of existingBox.items || []) {
                        if (!newItemIds.has(existingItem.id)) {
                            await partService.deletePackingSlipItem(existingItem.id);
                        }
                    }
                    for (const item of items) {
                        if (existingItemIds.has(item.id)) {
                            await partService.updatePackingSlipItem(item);
                        } else {
                            await partService.addItemToPackingSlip({
                                packingSlipId: existingBox.id,
                                purchasedPartId: item.purchasedPartId,
                                quantityExpected: item.quantityExpected,
                                quantityReceived: item.quantityReceived,
                            });
                        }
                    }
                    savedBoxIds.push(existingBox.id);
                    if (box.boxNumber === 1) primaryBoxId = existingBox.id;
                } else {
                    const formData = new FormData();
                    const packingSlipData = {
                        id: uuid(),
                        name: boxName,
                        trackingNumber: this.state.packingSlipTrackingNumber || null,
                        vendorName: this.state.selectedVendor,
                        dateCreated: toUtcForStorage(new Date()),
                        isReceived: false,
                        userId: this.state.user.id,
                        items,
                    };
                    formData.append('packingSlip', JSON.stringify(packingSlipData));
                    const createdPackingSlip = await partService.createPurchaseOrderPackingSlip(formData);
                    savedBoxIds.push(createdPackingSlip.id);
                    if (box.boxNumber === 1) primaryBoxId = createdPackingSlip.id;
                }
            }

            const keptBoxNumbers = new Set(boxesToProcess.map((box) => box.boxNumber));
            for (const existingBox of this.state.shipmentBoxes || []) {
                const boxNumber = isMulti ? getBoxNumberFromName(existingBox.name) : 1;
                if (!keptBoxNumbers.has(boxNumber)) {
                    await partService.deletePackingSlip(existingBox.id);
                }
            }

            await this.uploadEditedPackingSlipFiles(primaryBoxId || savedBoxIds[0], savedBoxIds);
            window.$alertSuccess(isMulti ? 'Shipment updated successfully' : 'Packing slip updated successfully');
            await this.loadData();
        } catch (error) {
            console.error('Error updating packing slip:', error);
            window.$alertError('Error updating packing slip');
        } finally {
            this.setState({ saving: false });
        }
    };

    loadReceivingActions = async (boxes) => {
        const partService = ServiceLocator.Instance.getPartService();
        const entries = await Promise.all((boxes || []).map(async (box) => {
            try {
                const actions = await partService.getReceivingActionsByPackingSlipId(box.id);
                return [box.id, actions || []];
            } catch (error) {
                console.error('Error loading receiving actions:', error);
                return [box.id, []];
            }
        }));
        this.setState({ receivingActionsByBox: Object.fromEntries(entries) });
    };

    refreshAfterReceive = async () => {
        const partService = ServiceLocator.Instance.getPartService();
        const packingSlipId = this.state.packingSlipId;
        const packingSlips = await partService.getAllPackingSlips();
        const current = packingSlips.find((slip) => slip.id === packingSlipId);
        if (!current) {
            globalProps.history.push('/admin/packing-slips');
            return;
        }

        const shipment = getShipmentBoxesForPackingSlip(packingSlips, current);
        this.setState({
            packingSlips,
            packingSlip: shipment.boxes.find((box) => box.id === packingSlipId) || current,
            shipmentBoxes: shipment.boxes,
            isMultiBoxShipment: shipment.isMultiBox,
            shipmentBaseName: shipment.baseName,
            receivingBoxId: null,
            receivingQuantities: {},
            isReceiving: false,
            editingReceivingActionId: null,
            editingReceivingAction: {},
        }, () => {
            this.loadReceivingActions(shipment.boxes);
            if (current.vendorName) {
                this.loadAllPurchasedPartsForVendor(current.vendorName);
            }
        });
    };

    getReceivingBoxes = () => {
        return [...(this.state.shipmentBoxes || [])].sort(
            (a, b) => getBoxNumberFromName(a.name) - getBoxNumberFromName(b.name)
        );
    };

    getBoxItemPart = (item) => {
        const purchased = (this.state.outstandingParts || []).find((part) => part.id === item.purchasedPartId)
            || item.purchasedPart;
        const catalog = purchased?.partId
            ? (this.state.parts || []).find((part) => part.part.id === purchased.partId)
            : null;
        return {
            partNumber: catalog?.part?.partId || purchased?.partNumber || purchased?.partFamilyName || 'Unknown',
            revision: catalog?.part?.revision || '',
            description: catalog?.part?.description || purchased?.partDescription || purchased?.partFamilyName || 'Unknown',
            purchaseOrderNumber: purchased?.purchaseOrderNumber || '-',
        };
    };

    getPartInfo = (item) => {
        const part = this.getBoxItemPart(item);
        const revision = part.revision ? ` rev ${part.revision}` : '';
        return {
            partId: `${part.partNumber}${revision}`,
            description: part.description,
        };
    };

    getShipmentViewModel = () => {
        const boxes = this.getReceivingBoxes().map((box) => ({
            ...box,
            isFullyReceived: isPackingSlipFullyReceived(box),
        }));
        const first = boxes[0] || {};
        return {
            baseName: this.state.shipmentBaseName || first.name || 'Packing Slip',
            vendorName: first.vendorName || this.state.selectedVendor || '',
            dateCreated: first.dateCreated,
            trackingNumber: first.trackingNumber || '',
            boxes,
            totalItems: boxes.reduce((sum, box) => sum + (box.items?.length || 0), 0),
            isFullyReceived: boxes.length > 0 && boxes.every((box) => isPackingSlipFullyReceived(box)),
            hasAnyReceiving: boxes.some((box) => box.hasAnyReceiving || (box.items || []).some((item) => (item.quantityReceived || 0) > 0)),
        };
    };

    openEditScreen = (boxNumber = 1) => {
        this.setState({
            screen: 'edit',
            viewingBoxId: null,
            currentBoxNumber: boxNumber || 1,
            receivingBoxId: null,
            receivingQuantities: {},
        });
    };

    openDuplicatePackingSlip = (duplicationData) => {
        globalProps.history.push({
            pathname: '/admin/packing-slips',
            state: { duplicatePackingSlip: duplicationData },
        });
    };

    downloadPackingSlipFile = (url, filename) => {
        if (!url) return;
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = () => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(xhr.response);
            link.setAttribute('download', filename || 'packing-slip-file');
            link.click();
        };
        xhr.open('GET', url);
        xhr.send();
    };

    startReceivingBox = (boxId) => {
        const box = this.getReceivingBoxes().find((entry) => entry.id === boxId);
        if (!box) return;
        const receivingQuantities = {};
        (box.items || []).forEach((item) => {
            receivingQuantities[item.id] = Math.max(0, (item.quantityExpected || 0) - (item.quantityReceived || 0));
        });
        this.setState({
            receivingBoxId: boxId,
            receivingQuantities,
            isReceiving: false,
        });
    };

    cancelReceivingBox = () => {
        this.setState({
            receivingBoxId: null,
            receivingQuantities: {},
            isReceiving: false,
        });
    };

    updateReceivingQuantity = (itemId, quantity) => {
        this.setState((prevState) => ({
            receivingQuantities: {
                ...prevState.receivingQuantities,
                [itemId]: quantity,
            },
        }));
    };

    fullyReceiveBox = async (boxId) => {
        const box = this.getReceivingBoxes().find((entry) => entry.id === boxId);
        if (!box) return;
        this.setState({ isReceiving: true });
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const userId = AuthenticationService.getInstance().getUserId();
            const outstandingQuantities = {};
            let hasOutstandingItems = false;
            (box.items || []).forEach((item) => {
                const outstanding = Math.max(0, (item.quantityExpected || 0) - (item.quantityReceived || 0));
                if (outstanding > 0) {
                    outstandingQuantities[item.id] = outstanding;
                    hasOutstandingItems = true;
                }
            });
            if (hasOutstandingItems) {
                await partService.receivePackingSlipWithQuantities(boxId, outstandingQuantities, userId);
            } else {
                await partService.receivePurchaseOrderPackingSlip(boxId, userId);
            }
            window.$alertSuccess(`Successfully received items for ${box.name}`);
            await this.refreshAfterReceive();
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            window.$alertError(error.response?.data?.error || 'Error receiving packing slip');
            this.setState({ isReceiving: false });
        }
    };

    submitReceiving = async (boxId) => {
        const box = this.getReceivingBoxes().find((entry) => entry.id === boxId);
        if (!box) return;
        const quantitiesToSubmit = Object.keys(this.state.receivingQuantities).reduce((acc, itemId) => {
            acc[itemId] = parseNumberInputValue(this.state.receivingQuantities[itemId]);
            return acc;
        }, {});
        this.setState({ isReceiving: true });
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const userId = AuthenticationService.getInstance().getUserId();
            await partService.receivePackingSlipWithQuantities(boxId, quantitiesToSubmit, userId);
            window.$alertSuccess(`Successfully received items for ${box.name}`);
            await this.refreshAfterReceive();
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            window.$alertError(error.response?.data?.error || 'Error receiving packing slip');
            this.setState({ isReceiving: false });
        }
    };

    deleteEntireShipment = async () => {
        const boxes = this.getReceivingBoxes();
        if (boxes.length === 0) return;
        const boxCount = boxes.length;
        const confirmed = window.confirm(
            `Are you sure you want to delete this entire shipment (${boxCount} ${boxCount === 1 ? 'box' : 'boxes'})? This action cannot be undone.`
        );
        if (!confirmed) return;

        this.setState({ deletingShipment: true });
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const results = await Promise.all(boxes.map(async (box) => {
                try {
                    await partService.deletePackingSlip(box.id);
                    return { success: true };
                } catch (error) {
                    const received = error.response?.status === 400
                        || error.response?.data?.error?.includes('Cannot delete a packing slip that has already been received');
                    return {
                        success: false,
                        boxName: box.name,
                        error: received ? 'Cannot delete a box that has already been received' : 'Error deleting box',
                    };
                }
            }));
            const errors = results.filter((result) => !result.success);
            if (errors.length > 0) {
                window.$alertError(`${errors[0].error}: ${errors.map((error) => error.boxName).join(', ')}`);
                if (errors.length < results.length) {
                    await this.refreshAfterReceive();
                }
            } else {
                window.$alertSuccess(`Entire shipment deleted successfully (${boxCount} ${boxCount === 1 ? 'box' : 'boxes'})`);
                globalProps.history.push('/admin/packing-slips');
            }
        } catch (error) {
            console.error('Error deleting entire shipment:', error);
            window.$alertError('Error deleting entire shipment');
        } finally {
            this.setState({ deletingShipment: false });
        }
    };

    deleteCurrentPackingSlip = async () => {
        const slip = this.state.packingSlip;
        if (!slip) return;
        const label = (slip.name || '').indexOf(' - Box ') > -1 ? 'box' : 'packing slip';
        const confirmed = window.confirm(`Are you sure you want to delete this ${label}? This action cannot be undone.`);
        if (!confirmed) return;

        this.setState({ deletingShipment: true });
        try {
            await ServiceLocator.Instance.getPartService().deletePackingSlip(slip.id);
            window.$alertSuccess(`${label.charAt(0).toUpperCase()}${label.slice(1)} deleted`);
            globalProps.history.push('/admin/packing-slips');
        } catch (error) {
            console.error('Error deleting packing slip:', error);
            const received = error.response?.status === 400
                || error.response?.data?.error?.includes('Cannot delete a packing slip that has already been received');
            window.$alertError(received ? `Cannot delete a ${label} that has already been received` : `Error deleting ${label}`);
        } finally {
            this.setState({ deletingShipment: false });
        }
    };

    duplicateCurrentPackingSlip = () => {
        const slip = this.state.packingSlip;
        if (!slip) return;
        globalProps.history.push({
            pathname: '/admin/packing-slips',
            state: {
                duplicatePackingSlip: {
                    action: 'duplicate',
                    packingSlip: {
                        name: slip.name,
                        vendorName: slip.vendorName,
                        trackingNumber: slip.trackingNumber || '',
                        fileUrl: slip.fileUrl || '',
                        fileName: slip.fileName || '',
                        items: (slip.items || []).map((item) => ({
                            purchasedPartId: item.purchasedPartId,
                            quantityExpected: Number(item.quantityExpected) || 0,
                            purchasedPart: item.purchasedPart,
                        })),
                    },
                },
            },
        });
    };

    saveReceivingAction = async (boxId, actionId) => {
        const existing = (this.state.receivingActionsByBox[boxId] || []).find((action) => action.id === actionId);
        if (!existing) return;
        try {
            await ServiceLocator.Instance.getPartService().updateReceivingAction(actionId, {
                ...existing,
                quantityReceived: parseInt(this.state.editingReceivingAction.quantityReceived, 10),
                notes: this.state.editingReceivingAction.notes,
            });
            window.$alertSuccess('Receiving action updated');
            await this.refreshAfterReceive();
        } catch (error) {
            console.error('Error updating receiving action:', error);
            window.$alertError('Error updating receiving action');
        }
    };

    deleteReceivingAction = async (actionId) => {
        const confirmed = window.confirm('Are you sure you want to delete this receiving action? This action cannot be undone.');
        if (!confirmed) return;
        try {
            await ServiceLocator.Instance.getPartService().deleteReceivingAction(actionId);
            window.$alertSuccess('Receiving action deleted');
            await this.refreshAfterReceive();
        } catch (error) {
            console.error('Error deleting receiving action:', error);
            window.$alertError('Error deleting receiving action');
        }
    };

    getShipmentSummaryRows = () => {
        const summaryByPart = {};
        this.getReceivingBoxes().forEach((box) => {
            const boxNumber = getBoxNumberFromName(box.name);
            (box.items || []).forEach((item) => {
                const partKey = item.purchasedPartId || item.id;
                const part = this.getBoxItemPart(item);
                if (!summaryByPart[partKey]) {
                    summaryByPart[partKey] = {
                        key: partKey,
                        partNumber: part.partNumber,
                        revision: part.revision,
                        description: part.description,
                        purchaseOrderNumber: part.purchaseOrderNumber,
                        quantityExpected: 0,
                        quantityReceived: 0,
                        boxNumbers: new Set(),
                    };
                }
                summaryByPart[partKey].quantityExpected += item.quantityExpected || 0;
                summaryByPart[partKey].quantityReceived += item.quantityReceived || 0;
                summaryByPart[partKey].boxNumbers.add(boxNumber);
            });
        });

        return Object.values(summaryByPart)
            .map((row) => ({
                ...row,
                boxCount: row.boxNumbers.size,
                quantityOutstanding: Math.max(0, row.quantityExpected - row.quantityReceived),
            }))
            .sort((a, b) => String(a.partNumber || '').localeCompare(String(b.partNumber || ''), undefined, { numeric: true, sensitivity: 'base' }));
    };

    renderShipmentDrawer = (shipment) => {
        const file = shipment.boxes[0];
        return (
            <div className="multi-box-shipment-view">
                <div style={{ marginBottom: 20, textAlign: 'right', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.openEditScreen(1)}
                        className="success-button-lg"
                    >
                        Edit Shipment
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={this.deleteEntireShipment}
                        disabled={this.state.deletingShipment}
                        className="delete-button-lg"
                    >
                        {this.state.deletingShipment ? 'Deleting...' : 'Delete Shipment'}
                    </Button>
                </div>
                <div className="shipment-overview">
                    <div className="shipment-overview-grid">
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">Vendor</Typography>
                            <Typography variant="body2" className="shipment-overview-value">{shipment.vendorName}</Typography>
                        </div>
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">Date Created</Typography>
                            <Typography variant="body2" className="shipment-overview-value">{formatUtcForDisplay(shipment.dateCreated)}</Typography>
                        </div>
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">Tracking</Typography>
                            <Typography variant="body2" className="shipment-overview-value">{shipment.trackingNumber || '-'}</Typography>
                        </div>
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">Boxes</Typography>
                            <Typography variant="body2" className="shipment-overview-value">{shipment.boxes.length}</Typography>
                        </div>
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">Items</Typography>
                            <Typography variant="body2" className="shipment-overview-value">{shipment.totalItems}</Typography>
                        </div>
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">Status</Typography>
                            <Typography variant="body2" className={`shipment-overview-value ${shipment.isFullyReceived ? 'received' : shipment.hasAnyReceiving ? 'partially-received' : ''}`}>
                                {shipment.isFullyReceived ? 'Fully Received' : shipment.hasAnyReceiving ? 'Partially Received' : 'Pending'}
                            </Typography>
                        </div>
                        <div className="shipment-overview-item">
                            <Typography variant="body2" className="shipment-overview-label">File</Typography>
                            <Typography variant="body2" className="shipment-overview-value">
                                {file?.fileUrl ? (
                                    <span
                                        className="shipment-overview-file"
                                        onClick={() => this.downloadPackingSlipFile(file.fileUrl, file.fileName)}
                                    >
                                        {file.fileName || file.fileUrl.split('/').pop() || 'Download File'}
                                    </span>
                                ) : '-'}
                            </Typography>
                        </div>
                    </div>
                </div>

                <div className="multi-box-view-tabs-container">
                    <Tabs
                        value={this.state.shipmentReceiveMode}
                        onChange={(event, mode) => {
                            if (!mode) return;
                            this.setState({
                                shipmentReceiveMode: mode,
                                receivingBoxId: mode === 'per-box' ? this.state.receivingBoxId : null,
                                receivingQuantities: mode === 'per-box' ? this.state.receivingQuantities : {},
                            });
                        }}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab value="per-box" label="Per Box View" />
                        <Tab value="complete-slip" label="Complete Packing Slip" />
                    </Tabs>
                </div>

                {this.state.shipmentReceiveMode === 'per-box' ? (
                    <>
                        <Typography variant="h6" className="individual-boxes-title">Box Details</Typography>
                        <div className="individual-boxes-grid">
                            {shipment.boxes.map((box) => {
                                const boxFullyReceived = isPackingSlipFullyReceived(box);
                                return (
                                    <div key={box.id} className={`individual-box ${boxFullyReceived ? 'received' : box.hasAnyReceiving ? 'partially-received' : 'not-received'}`}>
                                        <div className="box-header">
                                            <div className="box-header-left">
                                                <Typography variant="subtitle1" className="box-name">{box.name}</Typography>
                                                <Typography variant="body2" className={`box-status ${boxFullyReceived ? 'fully-received' : box.hasAnyReceiving ? 'partially-received' : 'not-received'}`}>
                                                    {boxFullyReceived ? 'FULLY RECEIVED' : box.hasAnyReceiving ? 'PARTIALLY RECEIVED' : 'NOT RECEIVED'}
                                                </Typography>
                                                {box.dateReceived ? (
                                                    <Typography variant="body2" className="box-items-count">
                                                        {`Received: ${formatUtcForDisplay(box.dateReceived)}`}
                                                    </Typography>
                                                ) : null}
                                                <Typography variant="body2" className="box-items-count">
                                                    {box.items?.length || 0} {box.items?.length === 1 ? 'item' : 'items'}
                                                </Typography>
                                            </div>
                                            <div className="box-header-right">
                                                {!boxFullyReceived && (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => this.fullyReceiveBox(box.id)}
                                                            disabled={this.state.isReceiving}
                                                            className="box-button"
                                                        >
                                                            RECEIVE ALL
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => this.startReceivingBox(box.id)}
                                                            disabled={this.state.isReceiving || this.state.receivingBoxId === box.id}
                                                            className="box-button"
                                                        >
                                                            CUSTOM RECEIVE
                                                        </Button>
                                                    </>
                                                )}
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => this.setState({ viewingBoxId: box.id })}
                                                        className="box-button view-edit"
                                                    >
                                                        VIEW
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => this.openEditScreen(getBoxNumberFromName(box.name))}
                                                        className="box-button edit"
                                                    >
                                                        EDIT
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        {box.items && box.items.length > 0 && (
                                            <div className="box-items-container">
                                                <table className="box-items-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Part Number</th>
                                                            <th className="description">Description</th>
                                                            <th className="expected">Expected</th>
                                                            <th className="received">Received</th>
                                                            {this.state.receivingBoxId === box.id && <th className="receive-qty">Receive Qty</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[...box.items].sort((a, b) => this.getPartInfo(a).partId.localeCompare(this.getPartInfo(b).partId)).map((item) => {
                                                            const partInfo = this.getPartInfo(item);
                                                            const remainingToReceive = item.quantityExpected - item.quantityReceived;
                                                            const receivingThisBox = this.state.receivingBoxId === box.id;
                                                            return (
                                                                <tr key={item.id}>
                                                                    <td className="text-left">{partInfo.partId}</td>
                                                                    <td className="description-ellipsis text-left">{partInfo.description}</td>
                                                                    <td style={{ textAlign: 'center' }}>{item.quantityExpected}</td>
                                                                    <td style={{ textAlign: 'center' }}>{item.quantityReceived}</td>
                                                                    {receivingThisBox && (
                                                                        <td style={{ textAlign: 'center' }}>
                                                                            <TextField
                                                                                type="number"
                                                                                value={this.state.receivingQuantities[item.id] || ''}
                                                                                onChange={(e) => this.updateReceivingQuantity(item.id, e.target.value)}
                                                                                variant="outlined"
                                                                                size="small"
                                                                                className="box-receiving-quantity"
                                                                                inputProps={{ min: 0, max: remainingToReceive }}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                                {this.state.receivingBoxId === box.id && (
                                                    <div className="receiving-controls">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={this.cancelReceivingBox}
                                                            disabled={this.state.isReceiving}
                                                            className="receiving-button"
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => this.submitReceiving(box.id)}
                                                            disabled={this.state.isReceiving}
                                                            className="receiving-button"
                                                        >
                                                            {this.state.isReceiving ? (
                                                                <>
                                                                    <CircularProgress size={16} className="receiving-loading" />
                                                                    Receiving...
                                                                </>
                                                            ) : 'Submit Receiving'}
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className="shipment-summary-view">
                        <Typography variant="h6" className="individual-boxes-title">Complete Packing Slip Summary</Typography>
                        <Typography variant="body2" className="shipment-summary-subtitle">
                            Combined totals across all boxes in this shipment.
                        </Typography>
                        <div className="box-items-container">
                            <table className="box-items-table shipment-summary-table">
                                <thead>
                                    <tr>
                                        <th>PO #</th>
                                        <th>Part Number</th>
                                        <th className="description">Description</th>
                                        <th className="expected">Expected</th>
                                        <th className="received">Received</th>
                                        <th className="expected">Outstanding</th>
                                        <th className="expected">Boxes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.getShipmentSummaryRows().map((row) => (
                                        <tr key={row.key}>
                                            <td>{row.purchaseOrderNumber}</td>
                                            <td>{row.revision ? `${row.partNumber} rev ${row.revision}` : row.partNumber}</td>
                                            <td className="description-ellipsis text-left">{row.description}</td>
                                            <td style={{ textAlign: 'center' }}>{row.quantityExpected}</td>
                                            <td style={{ textAlign: 'center' }}>{row.quantityReceived}</td>
                                            <td style={{ textAlign: 'center' }}>{row.quantityOutstanding}</td>
                                            <td style={{ textAlign: 'center' }}>{row.boxCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    renderViewHeading = (backLabel, onBack, title) => (
        <div style={{ marginBottom: 0, marginTop: -10 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <Button variant="outlined" className="success-button-lg" onClick={onBack}>{backLabel}</Button>
                <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                    <h1 style={{ margin: 0, color: 'white', fontSize: '1.75rem', whiteSpace: 'nowrap' }}>
                        {title}
                    </h1>
                </div>
            </div>
        </div>
    );

    renderDetailsPage = (boxId, backLabel, onBack, title) => {
        const tile = {
            maxWidth: 1600,
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        };
        return (
            <DashboardTemplate title={'Packing Slip'} onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    {this.renderViewHeading(backLabel, onBack, title)}
                    <div className="container" style={tile}>
                        <PackingSlipDetails
                            asPage
                            open
                            packingSlipId={boxId}
                            onClose={onBack}
                            onRequestEdit={(slip) => this.openEditScreen(getBoxNumberFromName(slip?.name, 1))}
                            onRequestDuplicate={this.openDuplicatePackingSlip}
                            onUpdate={this.refreshAfterReceive}
                            parts={this.state.parts}
                            partFamilies={this.state.partFamilies}
                            packingSlips={this.state.packingSlips}
                        />
                    </div>
                </div>
            </DashboardTemplate>
        );
    };

    renderViewPage = () => {
        if (!this.state.isMultiBoxShipment) {
            return this.renderDetailsPage(
                this.state.packingSlipId,
                'Back to Packing Slips',
                () => followAppLink(globalProps.history, '/admin/packing-slips'),
                this.state.packingSlip?.name || this.state.packingSlipName
            );
        }
        if (this.state.viewingBoxId) {
            const viewedBox = this.getReceivingBoxes().find((box) => box.id === this.state.viewingBoxId);
            const boxNumber = getBoxNumberFromName(viewedBox?.name);
            return this.renderDetailsPage(
                this.state.viewingBoxId,
                'Back to Shipment',
                () => this.setState({ viewingBoxId: null }),
                viewedBox?.name || `Box #${boxNumber}`
            );
        }

        const shipment = this.getShipmentViewModel();
        const tile = {
            maxWidth: 1600,
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        };
        return (
            <DashboardTemplate title={'Packing Slip'} onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    {this.renderViewHeading(
                        'Back to Packing Slips',
                        () => followAppLink(globalProps.history, '/admin/packing-slips'),
                        shipment.baseName
                    )}
                    <div className="container" style={tile}>
                        {this.renderShipmentDrawer(shipment)}
                    </div>
                </div>
            </DashboardTemplate>
        );
    };

    renderValidationErrors = () => {
        if (this.state.loadingOutstandingParts) return null;
        const validationErrors = this.getValidationErrors();
        if (validationErrors.length === 0) return null;

        const issueLabel = validationErrors.length === 1 ? '1 quantity issue' : `${validationErrors.length} quantity issues`;

        return (
            <div className="validation-errors">
                <details className="validation-errors-details">
                    <summary>
                        <span className="validation-errors-title">{issueLabel}</span>
                        {validationErrors.length === 1 && (
                            <span className="validation-errors-preview">
                                {validationErrors[0].partNumber} rev {validationErrors[0].revision}: {validationErrors[0].message}
                            </span>
                        )}
                    </summary>
                    <div className="validation-errors-list">
                        {validationErrors.map((error) => (
                            <div key={error.partId} className="validation-error-item">
                                <span className="validation-error-part">
                                    {error.partNumber} rev {error.revision}
                                </span>
                                <span className="validation-error-message">
                                    {error.message}
                                </span>
                            </div>
                        ))}
                    </div>
                </details>
                <FormControlLabel
                    className="validation-override"
                    control={(
                        <Checkbox
                            checked={this.state.overrideValidation}
                            onChange={(e) => this.setState({ overrideValidation: e.target.checked })}
                            color="primary"
                            size="small"
                        />
                    )}
                    label="Override and save anyway"
                />
            </div>
        );
    };

    renderFullShipmentSummary = () => {
        if (!this.state.isMultiBoxShipment || this.state.loadingOutstandingParts) return null;

        const { allSelectedParts, allSelectedQuantities } = this.getAllSelectedParts();
        const allPartsArray = Object.keys(allSelectedParts);
        if (allPartsArray.length === 0) return null;

        return (
            <div className="container" style={{ maxWidth: 1600, padding: 20, backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: 10 }}>
                <Typography variant="h6" className="full-shipment-summary-title" style={{ marginTop: 0 }}>
                    Full Shipment Contents Summary
                </Typography>
                <div className="full-shipment-summary-table parts-table-container-create">
                    <table className="parts-table">
                        <thead>
                            <tr>
                                <th>PO #</th>
                                <th style={{ textAlign: 'center' }}>Selected</th>
                                <th>Part Number</th>
                                <th>In Boxes</th>
                                <th style={{ textAlign: 'center' }}>Ordered</th>
                                <th style={{ textAlign: 'center' }}>Received</th>
                                <th style={{ textAlign: 'center' }}>Available</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allPartsArray.map((partId) => {
                                const part = this.state.outstandingParts.find((p) => p.id === partId);
                                const inBoxes = Object.keys(allSelectedParts[partId]).join(', ');
                                const isOveraged = this.isPartOveraged(partId);
                                return (
                                    <tr key={partId} className={isOveraged ? 'full-shipment-summary-row-overaged' : ''}>
                                        <td>{part?.purchaseOrderNumber || '-'}</td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: isOveraged ? '#f44336' : 'inherit' }}>
                                            {allSelectedQuantities[partId]}
                                        </td>
                                        <td>
                                            <PartDisplay
                                                part={{
                                                    partId: part?.partNumber || part?.partFamilyName || '-',
                                                    revision: part?.partId ? this.state.parts.find((x) => x.part.id === part.partId)?.part?.revision || '' : '',
                                                    description: part?.partDescription || part?.partFamilyName || 'Unknown',
                                                }}
                                                variant="table"
                                                classes={this.props.classes}
                                            />
                                        </td>
                                        <td className="full-shipment-summary-in-boxes">Box {inBoxes}</td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{part?.quantityOrdered || 0}</td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: (part?.quantityReceived || 0) > 0 ? 'green' : 'gray' }}>
                                            {part?.quantityReceived || 0}
                                        </td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: this.getOriginalAvailableQuantity(partId) > 0 ? 'green' : 'red' }}>
                                            {this.getOriginalAvailableQuantity(partId)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    selectionsSnapshot = (boxesSelectedParts, boxesSelectedPartsQuantities) => {
        const boxes = {};
        Object.keys(boxesSelectedParts || {}).forEach((boxNumber) => {
            const parts = boxesSelectedParts[boxNumber] || {};
            const quantities = (boxesSelectedPartsQuantities || {})[boxNumber] || {};
            boxes[boxNumber] = Object.keys(parts)
                .filter((partId) => parts[partId])
                .sort()
                .map((partId) => `${partId}:${quantities[partId] ?? ''}`);
        });
        return JSON.stringify(boxes);
    };

    checkForChanges = () => {
        const { initialSnapshot } = this.state;
        if (!initialSnapshot) return false;

        if (this.state.packingSlipName !== initialSnapshot.packingSlipName) return true;
        if ((this.state.packingSlipTrackingNumber || '') !== (initialSnapshot.packingSlipTrackingNumber || '')) return true;
        if ((this.state.packingSlipFiles || []).length > 0) return true;
        const initialBoxCount = initialSnapshot.numberOfBoxes || Object.keys(initialSnapshot.boxesSelectedParts || {}).length;
        if (this.state.numberOfBoxes !== initialBoxCount) return true;

        return this.selectionsSnapshot(this.state.boxesSelectedParts, this.state.boxesSelectedPartsQuantities)
            !== this.selectionsSnapshot(initialSnapshot.boxesSelectedParts, initialSnapshot.boxesSelectedPartsQuantities);
    };

    render() {
        const filteredParts = this.filterOutstandingParts(this.state.outstandingParts);
        const selectedCount = Object.keys(this.state.boxesSelectedParts[this.state.currentBoxNumber] || {}).length;
        const hasChanges = this.checkForChanges();

        if (this.state.loading) {
            return (
                <DashboardTemplate title="Packing Slip" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        if (this.state.screen !== 'edit') {
            return this.renderViewPage();
        }

        return (
            <DashboardTemplate title="Packing Slip" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root edit-packing-slip-box-page">
                    <div style={{ marginBottom: 0, marginTop: -10 }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 15, justifyContent: 'flex-start' }}>
                                <Button
                                    onClick={() => this.setState({ screen: 'view', viewingBoxId: null })}
                                    className="success-button-lg"
                                >
                                    Back
                                </Button>
                                {this.state.saving ? (
                                    <CircularProgress />
                                ) : (
                                    <Button
                                        onClick={this.handleSave}
                                        className="save-button-lg"
                                        style={{
                                            backgroundColor: hasChanges ? '#7fff84' : '#93e7eb',
                                            color: 'black',
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                )}
                            </div>

                            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                                <h1 style={{ margin: 0, color: 'white', fontSize: '1.75rem' }}>
                                    {this.state.packingSlipName}
                                </h1>
                                {this.state.isMultiBoxShipment && (
                                    <Typography variant="body2" style={{ color: '#ccc', marginTop: 4 }}>
                                        Editing box {this.state.currentBoxNumber} of {this.state.numberOfBoxes}
                                    </Typography>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="container" style={{ maxWidth: 1600, backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 10 }}>
                        <div className="edit-packing-slip-box-fields">
                            <div className="edit-packing-slip-box-field-row" style={{ paddingTop: 10 }}>
                                <TextField
                                    label={this.state.numberOfBoxes > 1 ? 'Shipment Name' : 'Packing Slip Name'}
                                    autoComplete="off"
                                    variant="outlined"
                                    value={this.state.packingSlipName}
                                    onChange={(e) => this.setState({ packingSlipName: e.target.value })}
                                    placeholder="Enter packing slip name..."
                                    fullWidth
                                />
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Boxes</InputLabel>
                                    <Select
                                        value={this.state.numberOfBoxes}
                                        onChange={(e) => this.handleNumberOfBoxesChange(parseInt(e.target.value, 10))}
                                        label="Boxes"
                                    >
                                        {Array.from({ length: 250 }, (_, index) => index + 1).map((num) => (
                                            <MenuItem key={num} value={num}>{num}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Tracking Number (Optional)"
                                    autoComplete="off"
                                    variant="outlined"
                                    value={this.state.packingSlipTrackingNumber}
                                    onChange={(e) => this.setState({ packingSlipTrackingNumber: e.target.value })}
                                    placeholder="Enter tracking number..."
                                    fullWidth
                                />
                                <TextField
                                    label="Vendor"
                                    autoComplete="off"
                                    variant="outlined"
                                    value={this.state.selectedVendor}
                                    fullWidth
                                    disabled
                                />
                            </div>
                            <div className="edit-packing-slip-box-file-section">
                                {this.state.originalFileName && (
                                    <Typography variant="body2" color="textSecondary" className="create-packing-slip-original-file">
                                        Current file:{' '}
                                        {this.state.originalFileUrl ? (
                                            <span
                                                className="packing-slip-file"
                                                onClick={() => this.downloadPackingSlipFile(this.state.originalFileUrl, this.state.originalFileName)}
                                                title="Download packing slip file"
                                            >
                                                {this.state.originalFileName}
                                            </span>
                                        ) : this.state.originalFileName}
                                    </Typography>
                                )}
                                <div className="create-packing-slip-file">
                                    <FilePond
                                        files={this.state.packingSlipFiles}
                                        onupdatefiles={(fileItems) => {
                                            this.setState({
                                                packingSlipFiles: fileItems.map((fileItem) => fileItem.file),
                                            });
                                        }}
                                        allowMultiple={false}
                                        maxFiles={1}
                                        allowFileTypeValidation={false}
                                        allowSyncAcceptAttribute={false}
                                        maxFileSize="25MB"
                                        labelIdle='(Optional) Drag & Drop your packing slip file or <span class="filepond--label-action">Browse</span>'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="edit-packing-slip-box-parts-section">
                            {this.renderValidationErrors()}

                            {this.state.numberOfBoxes > 1 && (
                                <div className="box-tabs-container">
                                    <div className="box-tabs-header">
                                        <div className="box-tabs-wrapper">
                                            {Array.from({ length: this.state.numberOfBoxes }, (_, index) => index + 1).map((boxNumber) => (
                                                <Button
                                                    key={boxNumber}
                                                    variant={this.state.currentBoxNumber === boxNumber ? 'contained' : 'outlined'}
                                                    color={this.state.currentBoxNumber === boxNumber ? 'primary' : 'default'}
                                                    onClick={() => this.handleBoxTabChange(boxNumber)}
                                                    className={`box-tab-button ${this.state.currentBoxNumber === boxNumber ? 'active' : 'inactive'}`}
                                                >
                                                    Box {boxNumber}
                                                    {Object.keys(this.state.boxesSelectedParts[boxNumber] || {}).length > 0 && (
                                                        <span className={`box-tab-count ${this.state.currentBoxNumber === boxNumber ? 'active' : 'inactive'}`}>
                                                            {Object.keys(this.state.boxesSelectedParts[boxNumber] || {}).length}
                                                        </span>
                                                    )}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="clone-actions">
                                        <Typography variant="body2" className="clone-actions-label">Clone Actions:</Typography>
                                        <FormControl variant="outlined" size="small" className="clone-select">
                                            <InputLabel>Clone To</InputLabel>
                                            <Select
                                                label="Clone To"
                                                value=""
                                                onChange={(e) => this.cloneSelectionsOntoBox(this.state.currentBoxNumber, parseInt(e.target.value, 10))}
                                            >
                                                {Array.from({ length: this.state.numberOfBoxes }, (_, index) => index + 1)
                                                    .filter((num) => num !== this.state.currentBoxNumber)
                                                    .map((num) => (
                                                        <MenuItem key={num} value={num}>Box {num}</MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl variant="outlined" size="small" className="clone-select">
                                            <InputLabel>Clone From</InputLabel>
                                            <Select
                                                label="Clone From"
                                                value=""
                                                onChange={(e) => this.cloneSelectionsOntoBox(parseInt(e.target.value, 10), this.state.currentBoxNumber)}
                                            >
                                                {Array.from({ length: this.state.numberOfBoxes }, (_, index) => index + 1)
                                                    .filter((num) => num !== this.state.currentBoxNumber)
                                                    .map((num) => (
                                                        <MenuItem key={num} value={num}>Box {num}</MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                            )}

                            <div className="parts-selection-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                                <Button variant="outlined" size="small" onClick={this.handleSelectAll}>Select All</Button>
                                <Button variant="outlined" size="small" onClick={this.handleSelectNone}>Select None</Button>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={this.state.showSelected}
                                            onChange={() => this.setState((prev) => ({ showSelected: !prev.showSelected }))}
                                            color="primary"
                                        />
                                    )}
                                    label="Show Selected"
                                />
                                <TextField
                                    label="Search by PN, PO #, or Description"
                                    autoComplete="off"
                                    variant="outlined"
                                    value={this.state.partsSearchTerm}
                                    onChange={(e) => this.setState({ partsSearchTerm: e.target.value, showSelected: false })}
                                    className="parts-search"
                                    size="small"
                                />
                                <Typography variant="body2" className="parts-selection-status">
                                    {this.state.showSelected
                                        ? `${filteredParts.length} selected parts shown`
                                        : `${selectedCount} of ${filteredParts.length} parts selected in Box ${this.state.currentBoxNumber}`}
                                </Typography>
                            </div>

                            {this.state.loadingOutstandingParts ? (
                                <center><CircularProgress /></center>
                            ) : (
                                <div className="parts-table-container-create">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: 50 }}>Select</th>
                                                <th style={{ minWidth: 60, cursor: 'pointer' }} onClick={() => this.handleSort('purchaseOrderNumber')}>
                                                    PO # {this.state.sortBy === 'purchaseOrderNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th style={{ minWidth: 160, cursor: 'pointer' }} onClick={() => this.handleSort('orderDate')}>
                                                    PO Date {this.state.sortBy === 'orderDate' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th style={{ minWidth: 110, cursor: 'pointer' }} onClick={() => this.handleSort('partNumber')}>
                                                    PN {this.state.sortBy === 'partNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th style={{ minWidth: 60 }}>Ordered</th>
                                                <th style={{ minWidth: 60 }}>Received</th>
                                                <th style={{ minWidth: 90 }}>On Other Slips</th>
                                                <th style={{ minWidth: 60 }}>Available</th>
                                                <th style={{ minWidth: 60 }}>Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredParts.map((part) => {
                                                const isOveraged = this.isPartOveraged(part.id);
                                                const isSelected = !!this.state.boxesSelectedParts[this.state.currentBoxNumber]?.[part.id];
                                                return (
                                                    <tr key={part.id} className={isOveraged ? 'parts-table-row-overaged' : ''}>
                                                        <td>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={(e) => this.handleBoxPartSelect(part.id, e.target.checked)}
                                                                style={{ color: isOveraged ? '#f44336' : undefined }}
                                                            />
                                                        </td>
                                                        <td><PurchaseLineLink part={part} /></td>
                                                        <td>{formatUtcForDisplay(part.orderDate)}</td>
                                                        <td>
                                                            <PartDisplay
                                                                part={{
                                                                    partId: part.partNumber || part.partFamilyName || '-',
                                                                    revision: this.state.parts.find((x) => x.part.id === part.partId)?.part?.revision || '',
                                                                    description: part.partDescription || part.partFamilyName || 'Unknown',
                                                                }}
                                                                variant="table"
                                                                classes={this.props.classes}
                                                            />
                                                        </td>
                                                        <td>{part.quantityOrdered}</td>
                                                        <td>{part.quantityReceived}</td>
                                                        <td style={{ color: this.getAdjustedOnOtherSlipsQuantity(part.id) > 0 ? 'orange' : 'inherit', fontWeight: this.getAdjustedOnOtherSlipsQuantity(part.id) > 0 ? 'bold' : 'normal' }}>
                                                            {this.getAdjustedOnOtherSlipsQuantity(part.id)}
                                                        </td>
                                                        <td style={{ color: this.getAdjustedAvailableQuantity(part.id) > 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                                                            {this.getAdjustedAvailableQuantity(part.id)}
                                                        </td>
                                                        <td>
                                                            {isSelected ? (
                                                                <TextField
                                                                    type="number"
                                                                    value={getNumberInputDisplayValue(this.state.boxesSelectedPartsQuantities[this.state.currentBoxNumber]?.[part.id])}
                                                                    onChange={(e) => this.handleBoxPartQuantityChange(part.id, e.target.value)}
                                                                    inputProps={{ min: 1 }}
                                                                    size="small"
                                                                    className={`parts-table-quantity-input ${isOveraged ? 'overaged' : ''}`}
                                                                    error={isOveraged}
                                                                />
                                                            ) : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {this.renderFullShipmentSummary()}
                </div>
            </DashboardTemplate>
        );
    }
}

EditPackingSlipBox.propTypes = {
    classes: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
};

export default withStyles(styles)(EditPackingSlipBox);
